










































































































































































































import { Invoice } from '@accounting/interfaces/invoice';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { Order } from '@accounting/interfaces/order';
import moment from 'moment';
import ModulesHelper from '@core/services/modules.helper';
import ToolsHelper from '@core/services/tools.helper';
import PdfPage from '../../pdf-page/pdf-page.vue';
import InvoiceFullDetailsLines from './invoice-full-details-lines.vue';
import InvoicePdfHeader from '../../invoice-pdf-header.vue';

interface PageInfo {
  totalLines: number;
  maxReached: boolean;
  orders: Order[];
}

@Component({
  name: 'bpc-invoice-full-details',
  components: {
    'bpc-pdf-page': PdfPage,
    'bpc-invoice-full-details-lines': InvoiceFullDetailsLines,
    'bpc-invoice-pdf-header': InvoicePdfHeader,
    // 'bpc-invoice-first-page': InvoiceFirstPage,
    // 'bpc-invoice-common-page': InvoiceCommonPage,
  },
})
export default class InvoiceFullDetails extends Vue {
  // NOTE: Using this props we can adjust the content to the pdf page, you can play
  // with this in order to fix the data to our needs
  @Prop({ type: Number, default: 30 }) readonly firstPageTotalLines: number;
  @Prop({ type: Number, default: 32 }) readonly commonPageTotalLines: number;
  @Prop({ type: Number, default: 3 }) readonly minLinesForTotalRow: number;
  @Prop({ type: Number, default: 5 }) readonly minLinesForFooter: number;
  @Prop({ type: Number, default: 349 }) readonly headerHeight: number; // heigth in px
  @Prop({ type: Number, default: 200 }) readonly footerHeight: number; // heigth in px

  @Prop(Object) readonly invoice: Invoice;
  @Prop(Boolean) readonly debug: boolean;
  @Prop(String) readonly invoiceCode: string;
  @Prop({ type: Boolean, default: false }) readonly groupSubventionOnInvoices: boolean;

  formatDate(date) {
    if (date) {
      return moment(String(date)).format('DD/MM/YYYY');
    }
  }
  formatTime(time): string {
    if (time) {
      return time.substr(0, 5);
    }
    return '';
  }
  formatPrice(value: string | number) {
    return ToolsHelper.formatPrice(value);
  }

  get totalRowInFirstPage(): boolean {
    return this.commonPagesOrders.length === 0;
  }

  get footerInFirstPage(): boolean {
    return this.commonPagesOrders.length === 0;
  }

  get totalRowNeedsNewPage(): boolean {
    // If the invoice is 1 page only check with firstPageTotalLines
    if (this.commonPagesOrders.length === 0) {
      return this.firstPageTotalLines - this.firstPageInfo.totalLines < this.minLinesForTotalRow;
    }

    return (
      this.commonPageTotalLines - this.lastCommonPageInfo.totalLines < this.minLinesForTotalRow
    );

    // If the invoice has more than a page check with the last page
  }

  get footerNeedsNewPage(): boolean {
    // If the invoice is 1 page only check with firstPageTotalLines
    if (this.commonPagesOrders.length === 0) {
      return (
        this.firstPageTotalLines - this.firstPageInfo.totalLines - this.minLinesForTotalRow <
        this.minLinesForFooter
      );
    }

    return (
      this.commonPageTotalLines - this.lastCommonPageInfo.totalLines - this.minLinesForTotalRow <
      this.minLinesForFooter
    );

    // If the invoice has more than a page check with the last page
  }

  get firstPageInfo(): PageInfo {
    return this.ordersByPages[0];
  }

  get firstPageOrders(): Order[] {
    return this.firstPageInfo?.orders || [];
  }

  get lastCommonPageInfo(): PageInfo {
    return this.commonPagesOrders.slice(-1)[0];
  }

  get lastCommonPageIndex(): number {
    return this.commonPagesOrders.length - 1;
  }

  get commonPagesOrders(): PageInfo[] {
    return this.ordersByPages.filter((page, index) => {
      return index !== 0;
    });
  }

  get ordersByPages(): PageInfo[] {
    const res = [this.getEmptyOrderPageInfo()];
    const orders =
      this.invoice?.orders
        .filter(o => o.status !== 'cancelled')
        .sort(this.compareBookingEventDateTime) || [];
    let currentIndex = 0;

    orders.forEach((order: Order) => {
      const maxLines = currentIndex === 0 ? this.firstPageTotalLines : this.commonPageTotalLines;
      const currentPage = res[currentIndex];
      const linesDiff = this.orderLinesAndPageLinesDiff(currentPage.totalLines, order, maxLines);
      if (linesDiff >= 0 && !currentPage.maxReached) {
        res[currentIndex] = this.addOrderToPageInfo(currentPage, order, linesDiff);
      } else {
        currentPage.maxReached = true;
        res[currentIndex] = currentPage;
        const newPage = this.getEmptyOrderPageInfo();
        const newLinesDiff = this.orderLinesAndPageLinesDiff(
          newPage.totalLines,
          order,
          this.commonPageTotalLines,
        );
        res[currentIndex + 1] = this.addOrderToPageInfo(newPage, order, newLinesDiff);
        currentIndex++;
      }
    });

    return res;
  }

  get totalPages(): number {
    const footerExtraPage = this.totalRowNeedsNewPage || this.footerNeedsNewPage ? 1 : 0;
    return this.lastCommonPageIndex + 2 + footerExtraPage;
  }

  compareBookingEventDateTime(a, b) {
    if (a.booking_event && b.booking_event) {
      if (
        a.booking_event.date < b.booking_event.date ||
        (a.booking_event.date == b.booking_event.date &&
          a.booking_event.start_time < b.booking_event.start_time)
      ) {
        return -1;
      } else if (
        a.booking_event.date > b.booking_event.date ||
        (a.booking_event.date == b.booking_event.date &&
          a.booking_event.start_time > b.booking_event.start_time)
      ) {
        return 1;
      } else {
        return 0;
      }
    } else if (a.booking_event && !b.booking_event) {
      return -1;
    } else if (!a.booking_event && b.booking_event) {
      return 1;
    }
    return 0;
  }

  getEmptyOrderPageInfo(): PageInfo {
    return { totalLines: 0, maxReached: false, orders: [] };
  }

  orderLinesAndPageLinesDiff(totalLines: number, order: Order, maxLines: number): number {
    const nLines = this.getOrderTotalLines(order);
    const newLines = totalLines + nLines;
    return maxLines - newLines;
  }

  addOrderToPageInfo(pageInfo: PageInfo, order: Order, linesDiff: number): PageInfo {
    const nLines = this.getOrderTotalLines(order);
    const newLines = pageInfo.totalLines + nLines;
    pageInfo.totalLines = newLines;
    pageInfo.orders.push(order);
    pageInfo.maxReached = linesDiff <= 0;

    return pageInfo;
  }

  addOrdersToPageInfoArray(pageInfo: PageInfo[], order: Order, position: number): PageInfo[] {
    const res = pageInfo;
    if (!res[position]) {
      res[position] = { totalLines: 0, maxReached: false, orders: [] };
    }
    res[position].orders.push(order);

    return res;
  }

  get invoiceCodeString(): string {
    return this.invoice.document_number
      ? '#' + this.invoice.document_number
      : this.invoice.code
      ? '#' + this.invoiceCode
      : this.invoiceCode;
  }

  getOrderTotalLines(order: Order) {
    const nItems = order?.order_items?.length || 0;
    return nItems + 1; // Because we always add a line with the title/desc
  }
  isTaxManagementModuleActivated(): boolean {
    return this.$modulesService.hasModuleActivated(ModulesHelper.TAX_MANAGEMENT_MODULE_NAME);
  }

  get invoiceText(): string {
    return 'global.invoice_type.' + this.invoice.invoice_type;
  }
  get invoiceDateText(): string {
    if (this.invoice.invoice_type == 'ticket') {
      return 'global.ticket_date.label';
    } else if (this.invoice.invoice_type == 'invoice') {
      return 'global.invoice_date.label';
    } else {
      return 'global.date.label';
    }
  }

  get invoiceHasSubvention(): boolean {
    return !!this.invoice?.orders.find(item => item.type === 'subvention');
  }

  get totalAmountOfSubventions(): number {
    let total = 0;
    this.invoice?.orders.forEach(item => {
      if (item.type === 'subvention') {
        total += item.total_price;
      }
    });
    return total;
  }

  get totalAmountWithoutSubvention(): number {
    let total = 0;
    this.invoice?.orders.forEach(item => {
      if (item.type !== 'subvention') {
        total += item.total_price;
      }
    });
    return total;
  }
}
